// ============================================================
// Stat Card — Dashboard stat display
// ============================================================

import { Box, Card, CardContent, Typography } from '@mui/material';
import { tokens } from '@/theme/tokens';
import { TrendingUp, TrendingDown } from 'lucide-react';
import useDashboardStore from '@/store/dashboardStore';
import { DATE_RANGES } from '@/components/filters/AnalyticsFilter';

const getComparisonLabel = (range) => {
  switch (range) {
    case DATE_RANGES.TODAY: return 'vs yesterday';
    case DATE_RANGES.YESTERDAY: return 'vs prev day';
    case DATE_RANGES.LAST_7_DAYS: return 'vs prev 7 days';
    case DATE_RANGES.LAST_30_DAYS: return 'vs prev 30 days';
    case DATE_RANGES.THIS_MONTH: return 'vs last month';
    case DATE_RANGES.LAST_MONTH: return 'vs prev month';
    case DATE_RANGES.LAST_3_MONTHS: return 'vs prev 3 months';
    case DATE_RANGES.LAST_6_MONTHS: return 'vs prev 6 months';
    case DATE_RANGES.THIS_ACADEMIC_YEAR: return 'vs last year';
    case DATE_RANGES.LAST_ACADEMIC_YEAR: return 'vs prev year';
    case DATE_RANGES.ALL_TIME: return 'overall';
    default: return 'vs prev period';
  }
};

export function StatCard({ title, value, icon: Icon, trend, trendValue, trendLabel, color = 'primary', onClick }) {
  const colorMap = {
    primary: tokens.colors.primary,
    success: tokens.colors.success,
    warning: tokens.colors.warning,
    error: tokens.colors.error,
    info: tokens.colors.info,
    secondary: tokens.colors.secondary,
  };

  const c = colorMap[color] || colorMap.primary;
  const isPositive = trend === 'up';
  
  const dateRange = useDashboardStore((state) => state.dateRange);
  const defaultTrendLabel = getComparisonLabel(dateRange);

  return (
    <Card
      onClick={onClick}
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: `all ${tokens.transitions.normal}`,
        '&:hover': {
          boxShadow: tokens.shadows.md,
          transform: onClick ? 'translateY(-2px)' : 'none',
        },
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ color: tokens.colors.text.muted, mb: 0.5, fontWeight: 500 }}
            >
              {title}
            </Typography>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: tokens.colors.text.primary, lineHeight: 1.2 }}
            >
              {value}
            </Typography>
            {trendValue && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'nowrap' }}>
                {isPositive ? (
                  <TrendingUp size={16} color={tokens.colors.success.main} />
                ) : (
                  <TrendingDown size={16} color={tokens.colors.error.main} />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: isPositive
                      ? tokens.colors.success.main
                      : tokens.colors.error.main,
                    fontWeight: 600,
                  }}
                >
                  {trendValue}
                </Typography>
                <Typography variant="caption" sx={{ color: tokens.colors.text.muted, whiteSpace: 'nowrap' }}>
                  {trendLabel || defaultTrendLabel}
                </Typography>
              </Box>
            )}
          </Box>
          {Icon && (
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: tokens.radius.md,
                backgroundColor: `${c.main}10`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icon size={24} color={c.main} />
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
